import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogPortal, DialogOverlay } from "./ui/dialog";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import FacultyDetailModal from "./FacultyDetailModal";
import { facultyProfiles } from "../mock";
import {
  X,
  Activity,
  HeartPulse,
  BarChart3,
  BookOpen,
  Brain,
  Users,
  FileText,
  FlaskConical,
  Building2,
  GraduationCap,
  ArrowUpRight,
  Sparkles,
} from "lucide-react";

const iconMap = {
  Activity,
  HeartPulse,
  BarChart3,
  BookOpen,
  Brain,
  Users,
};

const TABS = [
  { id: "overview", label: "Overview", icon: Sparkles },
  // Temporarily hidden - kept in code for future re-enable
  // { id: "faculty", label: "Faculty", icon: GraduationCap },
  // { id: "labs", label: "Labs", icon: Building2 },
  // { id: "papers", label: "Publications", icon: FileText },
  // { id: "projects", label: "Projects", icon: FlaskConical },
];

const initials = (name) =>
  name
    .replace(/^(Dr\.|Prof\.)\s*/i, "")
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("");

// Staggered fade-in helper
const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] },
});

// Tab content (parent) — orchestrates stagger of list items
const tabContainerVariants = {
  hidden: { opacity: 0, y: 10 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.35,
      ease: [0.22, 1, 0.36, 1],
      staggerChildren: 0.06,
      delayChildren: 0.08,
    },
  },
  exit: { opacity: 0, y: -6, transition: { duration: 0.2 } },
};

// List item — picks up parent stagger via shared variant names
const listItemVariants = {
  hidden: { opacity: 0, y: 10 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
  },
};

const StatusPill = ({ status, accent }) => {
  const isActive = /active|ongoing|pilot|deployed/i.test(status);
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-full"
      style={{
        background: isActive ? `${accent}22` : "#11111108",
        color: isActive ? accent : "#666",
        border: `1px solid ${isActive ? accent + "55" : "#00000015"}`,
      }}
    >
      <span
        className="w-1.5 h-1.5 rounded-full"
        style={{ background: isActive ? accent : "#999" }}
      />
      {status}
    </span>
  );
};

const DomainDetailModal = ({ domain, open, onOpenChange }) => {
  const [activeTab, setActiveTab] = useState("overview");
  const [facultyProfile, setFacultyProfile] = useState(null);

  if (!domain) return null;
  const Icon = iconMap[domain.icon] || Activity;
  const accent = domain.accent;

  const handleOpenChange = (next) => {
    if (!next) setActiveTab("overview");
    onOpenChange(next);
  };

  const openFaculty = (name) => {
    const p = facultyProfiles[name];
    if (p) setFacultyProfile(p);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogOverlay className="bg-black/70 backdrop-blur-md" />
        <DialogPrimitive.Content
          data-testid={`domain-modal-${domain.code.toLowerCase()}`}
          className="fixed left-1/2 top-1/2 z-50 w-[95vw] max-w-5xl h-[88vh] -translate-x-1/2 -translate-y-1/2 bg-[#FAF8F5] rounded-2xl shadow-2xl overflow-hidden border border-black/5 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 duration-300"
        >
          {/* Close */}
          <DialogPrimitive.Close
            data-testid="domain-modal-close"
            className="absolute top-5 right-5 z-30 w-9 h-9 rounded-full bg-black/5 hover:bg-black/10 backdrop-blur-md flex items-center justify-center text-gray-700 transition-all hover:rotate-90"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </DialogPrimitive.Close>

          {/* Decorative watermark — huge number */}
          <motion.div
            aria-hidden
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 0.06, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.5, ease: "easeOut" }}
            className="pointer-events-none absolute -right-12 -top-16 text-[280px] font-extralight leading-none select-none"
            style={{ color: accent }}
          >
            {String(domain.id).padStart(2, "0")}
          </motion.div>

          <div className="relative flex flex-col h-full">
            {/* Top meta bar */}
            <motion.div
              {...fade(0.08)}
              className="flex items-center justify-between px-8 md:px-14 pt-7 pb-3"
            >
              <div className="flex items-center gap-3">
                <span
                  className="px-2.5 py-1 text-[10px] font-mono tracking-[0.3em] uppercase text-white rounded-sm"
                  style={{ background: accent }}
                >
                  {domain.code}
                </span>
                <span className="hidden sm:inline text-[10px] font-mono uppercase tracking-[0.3em] text-gray-400">
                  Keimyung AI Research Center · Domain #{String(domain.id).padStart(2, "0")}
                </span>
              </div>
            </motion.div>

            {/* Header */}
            <header className="px-8 md:px-14 pt-2 pb-7">
              <div className="flex items-start gap-8">
                {/* Title block */}
                <div className="flex-1 min-w-0">
                  <motion.h2
                    {...fade(0.16)}
                    className="text-4xl md:text-5xl lg:text-6xl font-light text-gray-900 tracking-tight leading-[1.02]"
                  >
                    {domain.title}
                  </motion.h2>
                  <motion.p
                    {...fade(0.24)}
                    className="mt-4 text-sm md:text-base font-medium tracking-[0.05em]"
                    style={{ color: accent }}
                  >
                    {domain.tagline}
                  </motion.p>

                  {/* Inline stats strip */}
                  {domain.stats && (
                    <motion.div
                      {...fade(0.32)}
                      className="mt-7 flex flex-wrap items-baseline gap-x-8 gap-y-3 pt-5 border-t border-dashed border-gray-300"
                    >
                      {domain.stats.map((s, i) => (
                        <div key={s.label} className="flex items-baseline gap-2">
                          <span
                            className="text-2xl md:text-3xl font-light tracking-tight"
                            style={{ color: accent }}
                          >
                            {s.value}
                          </span>
                          <span className="text-[10px] uppercase tracking-[0.25em] text-gray-500">
                            {s.label}
                          </span>
                          {i < domain.stats.length - 1 && (
                            <span className="text-gray-200 ml-3 hidden sm:inline">
                              /
                            </span>
                          )}
                        </div>
                      ))}
                    </motion.div>
                  )}
                </div>

                {/* Tiny photo stamp */}
                <div className="hidden md:block flex-shrink-0">
                  <motion.div
                    layoutId={`stamp-${domain.code}`}
                    className="relative w-[140px] h-[140px] rounded-xl overflow-hidden shadow-md"
                    style={{ border: `1px solid ${accent}33` }}
                    transition={{ type: "spring", stiffness: 260, damping: 30 }}
                  >
                    <img
                      src={domain.image}
                      alt={domain.title}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div
                      className="absolute inset-0"
                      style={{
                        background: `linear-gradient(180deg, transparent 50%, ${accent}88)`,
                      }}
                    />
                    {/* Icon overlay */}
                    <div className="absolute bottom-2 right-2 w-8 h-8 rounded-full bg-white/95 backdrop-blur-sm flex items-center justify-center shadow-sm">
                      <Icon className="w-4 h-4" style={{ color: accent }} />
                    </div>
                    {/* corner ticks (passport-style) */}
                    <div
                      className="absolute top-1.5 left-1.5 w-3 h-3 border-l border-t"
                      style={{ borderColor: "#ffffffaa" }}
                    />
                    <div
                      className="absolute top-1.5 right-1.5 w-3 h-3 border-r border-t"
                      style={{ borderColor: "#ffffffaa" }}
                    />
                  </motion.div>
                </div>
              </div>
            </header>

            {/* Tabs — hidden when only one tab (Overview only) */}
            {TABS.length > 1 && (
              <motion.nav
                {...fade(0.4)}
                className="px-8 md:px-14 border-b border-gray-200 bg-white/40 backdrop-blur-sm"
              >
                <div className="flex gap-1 overflow-x-auto -mb-px">
                  {TABS.map((tab) => {
                    const TabIcon = tab.icon;
                    const isActive = activeTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        data-testid={`domain-tab-${tab.id}`}
                        onClick={() => setActiveTab(tab.id)}
                        className={`group relative flex items-center gap-2 py-3.5 text-sm font-medium tracking-tight whitespace-nowrap transition-colors ${
                          isActive ? "text-gray-900" : "text-gray-400 hover:text-gray-700"
                        }`}
                        style={{ marginRight: "1.75rem" }}
                      >
                        <TabIcon
                          className="w-3.5 h-3.5"
                          style={isActive ? { color: accent } : {}}
                        />
                        {tab.label}
                        <span
                          className="absolute left-0 right-0 -bottom-px h-[2px] transition-all"
                          style={{
                            background: isActive ? accent : "transparent",
                          }}
                        />
                      </button>
                    );
                  })}
                </div>
              </motion.nav>
            )}

            {/* Content */}
            <div
              className="flex-1 overflow-y-auto px-8 md:px-14 py-9"
              data-testid="domain-modal-content"
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  variants={tabContainerVariants}
                  initial="hidden"
                  animate="show"
                  exit="exit"
                >
                  {activeTab === "overview" && (
                    <OverviewTab domain={domain} accent={accent} />
                  )}
                  {activeTab === "faculty" && (
                    <FacultyTab faculty={domain.faculty} accent={accent} onOpenFaculty={openFaculty} />
                  )}
                  {activeTab === "labs" && (
                    <LabsTab labs={domain.labs} accent={accent} />
                  )}
                  {activeTab === "papers" && (
                    <PapersTab papers={domain.papers} accent={accent} />
                  )}
                  {activeTab === "projects" && (
                    <ProjectsTab projects={domain.projects} accent={accent} />
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>

      <FacultyDetailModal
        profile={facultyProfile}
        open={!!facultyProfile}
        onOpenChange={(next) => !next && setFacultyProfile(null)}
        accent={accent}
      />
    </Dialog>
  );
};

/* ----------------------- Tab sections ----------------------- */

const TabHeader = ({ children, accent, count }) => (
  <div className="flex items-end justify-between mb-7 pb-3 border-b border-dashed border-gray-300">
    <div className="flex items-baseline gap-3">
      <span
        className="text-xs font-mono uppercase tracking-[0.3em]"
        style={{ color: accent }}
      >
        §
      </span>
      <h3 className="text-2xl md:text-3xl font-light tracking-tight text-gray-900">
        {children}
      </h3>
    </div>
    {count !== undefined && (
      <span className="text-[10px] font-mono tracking-[0.25em] uppercase text-gray-400">
        {String(count).padStart(2, "0")} entries
      </span>
    )}
  </div>
);

const OverviewTab = ({ domain, accent }) => (
  <div className="max-w-3xl">
    <div className="relative">
      <span
        className="absolute -left-7 -top-1 text-5xl font-serif leading-none"
        style={{ color: accent }}
      >
        “
      </span>
      <p className="text-lg md:text-xl font-light leading-[1.7] text-gray-800">
        {domain.overview}
      </p>
    </div>

    <div className="mt-12 pt-7 border-t border-gray-200">
      <div className="text-[10px] uppercase tracking-[0.3em] text-gray-400 mb-3 font-medium">
        Focus
      </div>
      <p className="text-sm text-gray-700 font-light leading-relaxed">
        {domain.description}
      </p>
    </div>
  </div>
);

const FacultyTab = ({ faculty, accent, onOpenFaculty }) => (
  <div>
    <TabHeader accent={accent} count={faculty.length}>
      Faculty
    </TabHeader>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {faculty.map((f, i) => {
        const hasProfile = !!facultyProfiles[f.name];
        const cardCls = `group flex gap-4 p-5 bg-white rounded-xl border border-gray-100 transition-colors ${
          hasProfile
            ? "hover:border-gray-900 cursor-pointer text-left"
            : "hover:border-gray-300"
        }`;
        const inner = (
          <>
            <div
              className="w-12 h-12 flex-shrink-0 rounded-full flex items-center justify-center text-xs font-medium tracking-wider"
              style={{
                background: `${accent}1A`,
                color: accent,
                border: `1px solid ${accent}55`,
              }}
            >
              {initials(f.name)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <div className="font-medium text-gray-900 truncate">
                  {f.name}
                </div>
                {hasProfile && (
                  <ArrowUpRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-900 transition-colors flex-shrink-0" />
                )}
              </div>
              <div
                className="text-xs font-medium mt-0.5"
                style={{ color: accent }}
              >
                {f.role}
              </div>
              <div className="text-xs text-gray-500 mt-1">{f.affiliation}</div>
              <div className="text-xs text-gray-700 mt-2 font-light">
                {f.expertise}
              </div>
              {hasProfile && (
                <div
                  className="mt-2.5 text-[10px] font-mono uppercase tracking-[0.25em]"
                  style={{ color: accent }}
                >
                  View profile →
                </div>
              )}
            </div>
          </>
        );

        return hasProfile ? (
          <motion.button
            key={i}
            type="button"
            onClick={() => onOpenFaculty && onOpenFaculty(f.name)}
            variants={listItemVariants}
            data-testid={`faculty-card-${i}`}
            className={cardCls}
          >
            {inner}
          </motion.button>
        ) : (
          <motion.div
            key={i}
            variants={listItemVariants}
            data-testid={`faculty-card-${i}`}
            className={cardCls}
          >
            {inner}
          </motion.div>
        );
      })}
    </div>
  </div>
);

const LabsTab = ({ labs, accent }) => (
  <div>
    <TabHeader accent={accent} count={labs.length}>
      Participating Labs
    </TabHeader>
    <div className="space-y-3">
      {labs.map((lab, i) => {
        const Wrapper = lab.href ? motion.a : motion.div;
        const wrapperProps = lab.href
          ? { href: lab.href, target: "_blank", rel: "noopener noreferrer" }
          : {};
        return (
          <Wrapper
            key={i}
            {...wrapperProps}
            variants={listItemVariants}
            data-testid={`lab-card-${i}`}
            className={`group flex gap-4 p-5 bg-white rounded-xl border border-gray-100 hover:shadow-md transition-all ${
              lab.href ? "cursor-pointer hover:border-gray-300" : ""
            }`}
          >
            <div
              className="w-1 self-stretch rounded-full"
              style={{ background: accent }}
            />
            <div className="flex-1">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h4 className="font-medium text-gray-900 text-lg tracking-tight group-hover:underline decoration-1 underline-offset-4">
                    {lab.name}
                    {lab.fullName && (
                      <span className="text-gray-500 font-light"> ({lab.fullName})</span>
                    )}
                  </h4>
                </div>
                <ArrowUpRight className="w-4 h-4 text-gray-300 group-hover:text-gray-900 transition-all flex-shrink-0 mt-1 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </div>
              <div className="mt-2.5 text-sm space-y-1 font-light">
                <div className="text-gray-700">
                  <span className="text-gray-400 text-xs uppercase tracking-[0.18em] font-mono mr-2">Lead</span>
                  {lab.lead}
                </div>
                <div className="text-gray-700">
                  <span className="text-gray-400 text-xs uppercase tracking-[0.18em] font-mono mr-2">Focus</span>
                  {lab.focus}
                </div>
                {lab.href && (
                  <div
                    className="pt-2 text-[10px] font-mono uppercase tracking-[0.25em]"
                    style={{ color: accent }}
                  >
                    Visit lab homepage →
                  </div>
                )}
              </div>
            </div>
          </Wrapper>
        );
      })}
    </div>
  </div>
);

const PapersTab = ({ papers, accent }) => (
  <div>
    <TabHeader accent={accent} count={papers.length}>
      Selected Publications
    </TabHeader>
    <div className="space-y-1">
      {papers.map((p, i) => (
        <motion.div
          key={i}
          variants={listItemVariants}
          data-testid={`paper-item-${i}`}
          className="group grid grid-cols-[3rem_1fr_auto] gap-4 items-start py-4 border-b border-gray-200 last:border-b-0 hover:bg-white -mx-3 px-3 rounded-lg transition-colors"
        >
          <div
            className="text-xs font-mono pt-1"
            style={{ color: accent }}
          >
            {p.year}
          </div>
          <div className="min-w-0">
            <div className="font-medium text-gray-900 leading-snug">
              {p.title}
            </div>
            <div className="text-xs text-gray-500 mt-1.5 font-light">
              {p.authors}
            </div>
            <div className="text-xs italic text-gray-600 mt-1">{p.venue}</div>
          </div>
          <ArrowUpRight className="w-4 h-4 text-gray-300 group-hover:text-gray-900 transition-colors mt-1" />
        </motion.div>
      ))}
    </div>
  </div>
);

const ProjectsTab = ({ projects, accent }) => (
  <div>
    <TabHeader accent={accent} count={projects.length}>
      Active Projects
    </TabHeader>
    <div className="grid grid-cols-1 gap-3">
      {projects.map((p, i) => (
        <motion.div
          key={i}
          variants={listItemVariants}
          data-testid={`project-item-${i}`}
          className="p-5 bg-white rounded-xl border border-gray-100 hover:border-gray-300 transition-colors"
        >
          <div className="flex items-start justify-between gap-3 mb-2">
            <h4 className="font-medium text-gray-900 tracking-tight leading-snug">
              {p.title}
            </h4>
            <StatusPill status={p.status} accent={accent} />
          </div>
          {p.description && (
            <p className="text-sm text-gray-600 font-light mt-2">
              {p.description}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-3 text-xs text-gray-500">
            <span className="font-mono">{p.period}</span>
            {p.funding && (
              <span>
                <span className="text-gray-400">Funding · </span>
                {p.funding}
              </span>
            )}
          </div>
        </motion.div>
      ))}
    </div>
  </div>
);

export default DomainDetailModal;
